import { Injectable } from '@angular/core';
import { CampaignFilterRequest, FilterSettings, FilterSettingsWithSort } from './campaign.model';
import { Observable, of, Subject } from 'rxjs';
import { TriggerbeeApi } from '../core/triggerbee/triggerbee-api.enum';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { catchError, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PageTitle } from '../shared/utils/page-title';

@Injectable({
  providedIn: 'root',
})
export class CampaignFilterService {
  constructor(
    private http: HttpRequestDecorator,
    private translationService: TranslateService,
  ) {}

  public savedCampaignFilters: Array<CampaignFilterRequest> = [];
  public selectedFilter: CampaignFilterRequest;
  public unsavedFilter: CampaignFilterRequest;
  public hasUnsavedChanges = false;
  public unchangedFilter: CampaignFilterRequest;
  private url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/campaign-filters';
  public previousPage: PageTitle;
  public currentPage: PageTitle;

  public updatePage(page: PageTitle) {
    this.previousPage = this.currentPage;
    this.currentPage = page;
    if (this.previousPage !== this.currentPage) {
      // Reset current filter if the page has been changed.
      this.selectedFilter = this.getEmptyFilter();
    }
  }

  public getFilterById(id: number): CampaignFilterRequest {
    const filter = this.savedCampaignFilters.find((f) => f.id === id);
    return filter ? JSON.parse(JSON.stringify(filter)) : undefined;
  }

  public getUnsavedFilter(): CampaignFilterRequest {
    return this.unsavedFilter;
  }

  private openFilterSubject = new Subject<void>();
  openFilter$ = this.openFilterSubject.asObservable();

  private selectedFilterSubject = new Subject<CampaignFilterRequest>();
  selectedFilter$ = this.selectedFilterSubject.asObservable();

  private idWithChangesSubject = new Subject<number>();
  idWithChanges$ = this.idWithChangesSubject.asObservable();

  private allFiltersUpdaterSubject = new Subject<CampaignFilterRequest[]>();
  allFiltersUpdaterSubject$ = this.allFiltersUpdaterSubject.asObservable();

  public setIdWithChanges(id: number) {
    this.idWithChangesSubject.next(id);
  }

  public openFilter() {
    this.openFilterSubject.next();
  }

  public selectFilter(campaignFilterRequest: CampaignFilterRequest) {
    this.selectedFilterSubject.next(campaignFilterRequest);
  }

  private getAllFilters(currentPage: PageTitle): Observable<CampaignFilterRequest[]> {
    const staticFilters: CampaignFilterRequest[] = [];
    const currentYear = JSON.parse(JSON.stringify(this.createdCurrentYear));
    const lastYear = JSON.parse(JSON.stringify(this.createdLastYear));
    const pageFilterConfig: Partial<
      Record<PageTitle, { currentYearId: number; lastYearId: number; typeFilter: object }>
    > = {
      [PageTitle.Promotions]: {
        currentYearId: -5,
        lastYearId: -6,
        typeFilter: { isRegular: false, isSurvey: false, isForm: false, isPromotion: true, isReferral: false },
      },
      [PageTitle.Forms]: {
        currentYearId: -7,
        lastYearId: -8,
        typeFilter: { isRegular: false, isSurvey: false, isForm: true, isPromotion: false, isReferral: false },
      },
      [PageTitle.Surveys]: {
        currentYearId: -9,
        lastYearId: -10,
        typeFilter: { isRegular: false, isSurvey: true, isForm: false, isPromotion: false, isReferral: false },
      },
      [PageTitle.Quiz]: {
        currentYearId: -11,
        lastYearId: -12,
        typeFilter: { isQuiz: true },
      },
      [PageTitle.Games]: {
        currentYearId: -13,
        lastYearId: -14,
        typeFilter: { isGamification: true },
      },
      [PageTitle.NudgeTour]: {
        currentYearId: -15,
        lastYearId: -16,
        typeFilter: { isProductTour: true },
      },
    };

    if (currentPage === PageTitle.Campaigns) {
      staticFilters.push(this.forms);
      staticFilters.push(this.promotions);
    } else {
      const config = pageFilterConfig[currentPage];
      if (config) {
        currentYear.id = config.currentYearId;
        lastYear.id = config.lastYearId;
        currentYear.filterSettings.typeFilter = { ...config.typeFilter };
        lastYear.filterSettings.typeFilter = { ...config.typeFilter };
      }
    }
    staticFilters.push(currentYear);
    staticFilters.push(lastYear);
    if (currentPage === PageTitle.Backoffice) {
      staticFilters.push(this.publishedOnly);
      staticFilters.push(this.unPublishedOnly);
    }

    this.allFiltersUpdaterSubject$ = this.http.post(this.url + '/get-all', staticFilters).pipe(
      catchError((error) => {
        if (error.status === 500 || error.status === 405) {
          // Handle the error as needed, e.g., show a notification to the user
        }
        return of([]); // Return an empty array or a default value
      }),
    );

    return this.allFiltersUpdaterSubject$;
  }

  public fetchBackOfficeFilters(): Observable<CampaignFilterRequest[]> {
    this.savedCampaignFilters = [];
    this.savedCampaignFilters.push(this.createdCurrentYear);
    this.savedCampaignFilters.push(this.createdLastYear);
    this.savedCampaignFilters.push(this.publishedOnly);
    this.savedCampaignFilters.push(this.unPublishedOnly);
    return new Observable<CampaignFilterRequest[]>((subscriber) => {
      subscriber.next(this.savedCampaignFilters);
    });
  }

  public fetchAndStoreFilters(currentPage: PageTitle): Observable<CampaignFilterRequest[]> {
    this.resetUnsavedFilter();
    return this.getAllFilters(currentPage).pipe(tap((filters) => (this.savedCampaignFilters = filters)));
  }

  updateSavedCampaignFilter(filter: CampaignFilterRequest) {
    const index = this.savedCampaignFilters.findIndex((f) => f.id === filter.id);
    if (index > -1) {
      const cleanedUpFilter = CampaignFilterService.cleanUpFilter(filter.filterSettings);
      const postFilter = {
        name: filter.name,
        filterSettings: cleanedUpFilter,
      };
      this.http.put(this.url + '/' + filter.id, postFilter).subscribe((response) => {
        this.savedCampaignFilters[index] = response;
        this.resetUnsavedFilter();
        this.allFiltersUpdaterSubject.next(this.savedCampaignFilters);
        this.selectFilter(response);
      });
    }
  }

  deleteSavedCampaignFilter(filterId: number) {
    if (filterId == undefined) {
      return;
    }
    const index = this.savedCampaignFilters.findIndex((f) => f.id === filterId);
    if (index > -1) {
      this.http.delete(this.url + '/' + filterId).subscribe(() => {
        this.savedCampaignFilters.splice(index, 1);
        this.resetUnsavedFilter();
        this.selectFilter(undefined);
        this.allFiltersUpdaterSubject.next(this.savedCampaignFilters);
      });
    }
  }

  public static cleanUpFilter(filterSettings: FilterSettings) {
    const cleanedFilter = JSON.parse(JSON.stringify(filterSettings));

    if (cleanedFilter?.nameFilter && !cleanedFilter.nameFilter.substring) {
      cleanedFilter.nameFilter = null;
    }

    if (cleanedFilter.typeFilter && Object.values(cleanedFilter.typeFilter).every((value) => value === false)) {
      cleanedFilter.typeFilter = null;
    }

    if (cleanedFilter.tags && cleanedFilter.tags.length === 0) {
      cleanedFilter.tags = null;
    }

    if (cleanedFilter?.statusFilter && Object.values(cleanedFilter.statusFilter).every((value) => value === false)) {
      cleanedFilter.statusFilter = null;
    }

    if (
      cleanedFilter?.componentFilter &&
      Object.values(cleanedFilter.componentFilter).every((value) => value === false)
    ) {
      cleanedFilter.componentFilter = null;
    }

    if (cleanedFilter.accountFilter && cleanedFilter.accountFilter.siteIds.length === 0) {
      cleanedFilter.accountFilter.siteIds = null;
    }
    return cleanedFilter;
  }

  public static cleanUpFilterWithSort(filterSettings: FilterSettingsWithSort) {
    const cleanedFilter = JSON.parse(JSON.stringify(filterSettings));

    if (cleanedFilter?.nameFilter && !cleanedFilter.nameFilter.substring) {
      cleanedFilter.nameFilter = null;
    }

    if (cleanedFilter.typeFilter && Object.values(cleanedFilter.typeFilter).every((value) => value === false)) {
      cleanedFilter.typeFilter = null;
    }

    if (cleanedFilter.tags && cleanedFilter.tags.length === 0) {
      cleanedFilter.tags = null;
    }

    if (cleanedFilter?.statusFilter && Object.values(cleanedFilter.statusFilter).every((value) => value === false)) {
      cleanedFilter.statusFilter = null;
    }

    if (
      cleanedFilter?.componentFilter &&
      Object.values(cleanedFilter.componentFilter).every((value) => value === false)
    ) {
      cleanedFilter.componentFilter = null;
    }

    if (cleanedFilter.accountFilter && cleanedFilter.accountFilter.siteIds.length === 0) {
      cleanedFilter.accountFilter.siteIds = null;
    }
    return cleanedFilter;
  }

  createNewCampaignFilter(filter: CampaignFilterRequest) {
    const cleanedUpFilter = CampaignFilterService.cleanUpFilter(filter.filterSettings);

    const postFilter = {
      name: filter.name,
      filterSettings: cleanedUpFilter,
    };

    this.http.post(this.url, postFilter).subscribe((response: CampaignFilterRequest) => {
      this.savedCampaignFilters.push(response);
      this.selectedFilter = response;
      this.selectFilter(response);
      this.resetUnsavedFilter();
      this.allFiltersUpdaterSubject.next(this.savedCampaignFilters);
    });
  }

  prepareFilterForDisplay(filter: CampaignFilterRequest) {
    if (!filter && !filter?.filterSettings) {
      return;
    }
    if (!filter.filterSettings.statusFilter) {
      filter.filterSettings.statusFilter = {
        isPublished: false,
        isUnPublished: false,
        isDraft: false,
        isScheduledByDate: false,
        isScheduledByDaysAndHours: false,
        isScheduled: false,
        isFinished: false,
      };
    } else {
      if (
        filter.filterSettings.statusFilter.isScheduledByDate ||
        filter.filterSettings.statusFilter.isScheduledByDaysAndHours
      ) {
        filter.filterSettings.statusFilter.isScheduled = true;
      }
    }

    if (!filter.filterSettings.typeFilter) {
      filter.filterSettings.typeFilter = {
        isRegular: false,
        isSurvey: false,
        isForm: false,
        isPromotion: false,
        isReferral: false,
        isAbTest: false,
        isProductTour: false,
        isQuiz: false,
        isNudge: false,
        isGamification: false,
      };
    }

    if (!filter?.filterSettings?.nameFilter) {
      filter.filterSettings.nameFilter = {};
    }

    if (!filter?.filterSettings?.nameFilter?.substring) {
      filter.filterSettings.nameFilter = {};
    }

    if (!filter.filterSettings.componentFilter) {
      filter.filterSettings.componentFilter = {
        hasInputFields: false,
        variations: 0,
        variationsTo: 0,
        steps: 0,
        stepsTo: 0,
      };
    }
  }

  getLocalFilter() {
    if (!this.selectedFilter) {
      this.selectedFilter = this.getEmptyFilter();
    }
    this.prepareFilterForDisplay(this.selectedFilter);
    return this.selectedFilter;
  }

  getEmptyFilter(): CampaignFilterRequest {
    return {
      name: undefined,
      id: undefined,
      count: undefined,
      filterSettings: {
        typeFilter: undefined,
        createdTime: undefined,
        statusFilter: undefined,
        lastPublished: undefined,
        lastUpdated: undefined,
        tags: undefined,
        componentFilter: undefined,
        statisticsFilter: undefined,
        nameFilter: {},
      },
    };
  }

  getPreparedEmptyFilter(): CampaignFilterRequest {
    const filter = this.getEmptyFilter();
    this.prepareFilterForDisplay(filter);
    return filter;
  }

  private forms: CampaignFilterRequest = {
    name: this.translationService.instant('generic.forms'),
    id: -1,
    count: 0,
    filterSettings: {
      typeFilter: {
        isRegular: false,
        isForm: true,
        isSurvey: false,
        isReferral: false,
        isPromotion: false,
        isAbTest: false,
        isProductTour: false,
        isQuiz: false,
        isNudge: false,
        isGamification: false,
      },
    },
  };

  private promotions: CampaignFilterRequest = {
    name: this.translationService.instant('generic.promotions'),
    id: -2,
    count: 0,
    filterSettings: {
      typeFilter: {
        isRegular: false,
        isForm: false,
        isSurvey: false,
        isReferral: false,
        isPromotion: true,
        isAbTest: false,
        isProductTour: false,
        isQuiz: false,
        isNudge: false,
        isGamification: false,
      },
    },
  };

  private createdCurrentYear: CampaignFilterRequest = {
    name: this.translationService.instant('campaign.filter.created') + ' ' + new Date().getFullYear(),
    id: -3,
    count: 0,
    filterSettings: {
      createdTime: {
        from: this.getFirstDayOfCurrentYear(),
        to: this.getLastDayOfCurrentYear(),
      },
    },
  };

  private createdLastYear: CampaignFilterRequest = {
    name: this.translationService.instant('campaign.filter.created') + ' ' + (new Date().getFullYear() - 1),
    id: -4,
    count: 0,
    filterSettings: {
      createdTime: {
        from: this.getFirstDayOfLastYear(),
        to: this.getLastDayOfLastYear(),
      },
    },
  };
  private publishedOnly: CampaignFilterRequest = {
    name: this.translationService.instant('campaign.status-Published-2'),
    id: -5,
    count: 0,
    filterSettings: {
      statusFilter: {
        isDraft: false,
        isPublished: true,
        isUnPublished: false,
        isScheduledByDate: false,
        isScheduledByDaysAndHours: false,
        isScheduled: false,
        isFinished: false,
      },
    },
  };
  private unPublishedOnly: CampaignFilterRequest = {
    name: this.translationService.instant('campaign.status-Unpublished'),
    id: -6,
    count: 0,
    filterSettings: {
      statusFilter: {
        isDraft: false,
        isPublished: false,
        isUnPublished: true,
        isScheduledByDate: false,
        isScheduledByDaysAndHours: false,
        isScheduled: false,
        isFinished: false,
      },
    },
  };

  private getFirstDayOfCurrentYear(): string {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), 0, 1);
    return firstDay.toISOString().split('T')[0];
  }

  private getLastDayOfCurrentYear(): string {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), 11, 31);
    return lastDay.toISOString().split('T')[0];
  }

  private getFirstDayOfLastYear(): string {
    const now = new Date();
    const firstDay = new Date(now.getFullYear() - 1, 0, 1);
    return firstDay.toISOString().split('T')[0];
  }

  private getLastDayOfLastYear(): string {
    const now = new Date();
    const lastDay = new Date(now.getFullYear() - 1, 11, 31);
    return lastDay.toISOString().split('T')[0];
  }

  resetUnsavedFilter() {
    this.unsavedFilter = undefined;
    this.hasUnsavedChanges = false;
    this.unchangedFilter = undefined;
    this.setIdWithChanges(undefined);
  }

  getFilterForDisplay(filterId: number | undefined, pageTitle: PageTitle = undefined): CampaignFilterRequest {
    if (filterId == undefined) {
      this.unchangedFilter = JSON.parse(JSON.stringify(this.getPreparedEmptyFilter()));
      const filter = this.getLocalFilter();
      if (pageTitle === PageTitle.Backoffice || pageTitle === PageTitle.Campaigns) {
        return filter;
      }
      const isForms = pageTitle === PageTitle.Forms;
      const isPromotions = pageTitle === PageTitle.Promotions;
      const isReferral = pageTitle === PageTitle.Referral;
      const isSurvey = pageTitle === PageTitle.Surveys;
      const isSticky = pageTitle === PageTitle.NudgeTour;
      const isQuiz = pageTitle === PageTitle.Quiz;
      const isGamification = pageTitle === PageTitle.Games;
      filter.filterSettings.typeFilter = {
        isRegular: false,
        isSurvey: isSurvey,
        isForm: isForms,
        isPromotion: isPromotions,
        isReferral: isReferral,
        isAbTest: false,
        isProductTour: isSticky,
        isQuiz: isQuiz,
        isNudge: false,
        isGamification: isGamification,
      };
      return filter;
    }

    const freshFilter = this.getFilterById(filterId);
    this.prepareFilterForDisplay(freshFilter);
    this.unchangedFilter = JSON.parse(JSON.stringify(freshFilter));

    if (this?.unsavedFilter?.id == filterId) {
      this.hasUnsavedChanges =
        JSON.parse(JSON.stringify(freshFilter)) !== JSON.parse(JSON.stringify(this.unsavedFilter));
      return this.unsavedFilter;
    }

    this.hasUnsavedChanges = false;
    return (this.unsavedFilter = freshFilter);
  }

  resetLocalFilter() {
    this.selectedFilter = this.getPreparedEmptyFilter();
  }
}
